import { useEffect, useState } from "react";
import Button from "../components/Button";
import Card from "../components/Card";
import Input from "../components/Input";
import Navbar from "../components/Navbar";
import Title from "../components/Title";
import { apiUrl } from "../utils/api";

function ShopLogin({ setPage }) {
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const [shopPassword, setShopPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [shopAuthenticated, setShopAuthenticated] = useState(false);
  const [shopName, setShopName] = useState("");
  const [error, setError] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentShopPassword, setCurrentShopPassword] = useState("");
  const [newShopPassword, setNewShopPassword] = useState("");
  const [confirmShopPassword, setConfirmShopPassword] = useState("");
  const [passwordChangeError, setPasswordChangeError] = useState("");
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState("");

  const restoreShopSession = () => {
    const storedShopToken = localStorage.getItem("shopToken");
    const storedShopName = localStorage.getItem("shopName");
    if (storedShopToken && storedShopName) {
      setShopAuthenticated(true);
      setShopName(storedShopName);
    }
  };

  const selectedShop = shops.find((shop) => shop.id === shopId);
  const shopHint = selectedShop?.name === "むさぽ大阪"
    ? "むさぽ大阪では、これまでの従業員とシフト情報を確認できます。"
    : selectedShop?.name === "テスト店"
    ? "テスト店では、新しい店舗の従業員とシフトを作成・検証できます。"
    : "店舗を選択して、ログインしてください。";

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const res = await fetch(apiUrl("/shops"));
        const data = await res.json();
        if (res.ok) {
          setShops(data);
          setShopId((current) => current || (data.length > 0 ? data[0].id : ""));
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchShops();
    restoreShopSession();
  }, []);

  const handleShopLogin = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(apiUrl("/shop-login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shopId, password: shopPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "店舗ログインに失敗しました");
        return;
      }
      localStorage.setItem("shopToken", data.shopToken);
      localStorage.setItem("shopId", data.shop.id);
      localStorage.setItem("shopName", data.shop.name);
      setShopAuthenticated(true);
      setShopName(data.shop.name);
      setShopPassword("");
      setPage("login");
    } catch (error) {
      console.error(error);
      setError("店舗ログインエラー");
    } finally {
      setLoading(false);
    }
  };

  const handleShopPasswordChange = async () => {
    if (!shopAuthenticated) {
      setPasswordChangeError("店舗ログインしてください");
      return;
    }
    if (!currentShopPassword || !newShopPassword || !confirmShopPassword) {
      setPasswordChangeError("すべての項目を入力してください");
      setPasswordChangeSuccess("");
      return;
    }
    if (newShopPassword !== confirmShopPassword) {
      setPasswordChangeError("新しいパスワードが一致しません");
      setPasswordChangeSuccess("");
      return;
    }

    try {
      setLoading(true);
      setPasswordChangeError("");
      setPasswordChangeSuccess("");
      const shopToken = localStorage.getItem("shopToken");
      const res = await fetch(apiUrl("/shop-password"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-shop-token": shopToken,
        },
        body: JSON.stringify({
          currentPassword: currentShopPassword,
          newPassword: newShopPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPasswordChangeError(data.message || "変更に失敗しました");
        return;
      }
      setPasswordChangeSuccess(data.message || "店舗パスワードを変更しました");
      setCurrentShopPassword("");
      setNewShopPassword("");
      setConfirmShopPassword("");
      setShowChangePassword(false);
    } catch (error) {
      console.error(error);
      setPasswordChangeError("店舗パスワード変更中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  const clearShopSession = () => {
    localStorage.removeItem("shopToken");
    localStorage.removeItem("shopId");
    localStorage.removeItem("shopName");
    setShopAuthenticated(false);
    setShopName("");
    setShopPassword("");
  };

  return (
    <div>
      <Navbar />
      <main className="page-shell" style={{ maxWidth: "1120px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: "20px", alignItems: "start" }}>
          <Card className="p-5 sm:p-6">
            <Title>店舗ログイン</Title>
            <p style={{ margin: "0 0 18px", color: "#64748b", fontSize: "14px" }}>
              先に店舗ログインしてください。ログイン後、従業員IDでアプリに入ることができます。
            </p>
            {shopAuthenticated ? (
              <div style={{ marginBottom: "18px", padding: "16px", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#f8fafc" }}>
                <p style={{ margin: 0, color: "#334155", fontSize: "14px", fontWeight: 700 }}>
                  店舗ログイン済み
                </p>
                <p style={{ margin: "8px 0 0", color: "#475569", fontSize: "14px" }}>
                  現在の店舗: {shopName}
                </p>
                <div style={{ marginTop: "16px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <Button size="full" onClick={() => setPage("login")}>従業員ログインへ</Button>
                  <Button color="light" size="full" onClick={() => setShowChangePassword((prev) => !prev)}>
                    {showChangePassword ? "変更フォームを閉じる" : "店舗パスワード変更"}
                  </Button>
                  <Button color="light" size="full" onClick={clearShopSession}>店舗切替</Button>
                </div>
                {showChangePassword && (
                  <div style={{ marginTop: "16px", padding: "16px", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#fff" }}>
                    <p style={{ margin: 0, color: "#334155", fontSize: "14px", fontWeight: 700 }}>
                      店舗パスワードを変更
                    </p>
                    <div style={{ display: "grid", gap: "12px", marginTop: "12px" }}>
                      <label style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>
                        現在のパスワード
                        <Input
                          className="mt-1 w-full"
                          type="password"
                          placeholder="現在のパスワード"
                          value={currentShopPassword}
                          onChange={(e) => setCurrentShopPassword(e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>
                        新しいパスワード
                        <Input
                          className="mt-1 w-full"
                          type="password"
                          placeholder="新しいパスワード"
                          value={newShopPassword}
                          onChange={(e) => setNewShopPassword(e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>
                        新しいパスワード（確認）
                        <Input
                          className="mt-1 w-full"
                          type="password"
                          placeholder="新しいパスワードを再入力"
                          value={confirmShopPassword}
                          onChange={(e) => setConfirmShopPassword(e.target.value)}
                        />
                      </label>
                      {passwordChangeError && (
                        <p style={{ color: "#b91c1c", margin: 0, fontSize: "13px" }}>
                          {passwordChangeError}
                        </p>
                      )}
                      {passwordChangeSuccess && (
                        <p style={{ color: "#16a34a", margin: 0, fontSize: "13px" }}>
                          {passwordChangeSuccess}
                        </p>
                      )}
                      <Button size="full" onClick={handleShopPasswordChange} disabled={loading}>
                        {loading ? "変更中..." : "パスワードを変更する"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: "grid", gap: "16px", marginBottom: "18px" }}>
                <label style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>
                  店舗
                  <select
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 bg-white text-sm"
                    value={shopId}
                    onChange={(e) => setShopId(e.target.value)}
                  >
                    {shops.map((shop) => (
                      <option key={shop.id} value={shop.id}>{shop.name}</option>
                    ))}
                  </select>
                </label>
                <p style={{ margin: "4px 0 12px", color: "#475569", fontSize: "13px" }}>
                  {shopHint}
                </p>
                <label style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>
                  店舗パスワード
                  <Input
                    className="mt-1 w-full"
                    type="password"
                    placeholder="店舗パスワード"
                    value={shopPassword}
                    onChange={(e) => setShopPassword(e.target.value)}
                  />
                </label>
                {error && <p style={{ color: "#b91c1c", margin: 0, fontSize: "13px" }}>{error}</p>}
                <Button size="full" onClick={handleShopLogin} disabled={loading}>
                  {loading ? "ログイン中..." : "店舗ログイン"}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}

export default ShopLogin;
